<template>
  <div class="pivot-dial" :style="{ width: size + 'px', height: size + 'px' }">
    <svg viewBox="0 0 100 100" width="100%" height="100%">
      <circle class="pivot-dial-track" cx="50" cy="50" r="42" />
      <path v-if="arcPath" class="pivot-dial-progress" :d="arcPath" :stroke="color" />
      <line
        class="pivot-dial-needle"
        x1="50"
        y1="50"
        x2="50"
        y2="14"
        :stroke="color"
        :transform="`rotate(${angle} 50 50)`"
      />
      <circle
        v-if="pulsing"
        class="pivot-dial-pulse"
        cx="50"
        cy="50"
        r="5"
        :fill="color"
      />
      <circle class="pivot-dial-hub" cx="50" cy="50" r="5" :fill="color" />
    </svg>
    <div v-if="showModeBadge" class="pivot-dial-mode-badge" :class="{ 'pivot-dial-mode-badge--pulse': pulsing }"
      :style="{ background: modeColor }">
      <svg viewBox="0 0 24 24" class="pivot-dial-mode-icon">
        <path d="M12 2C12 2 5 10.5 5 15A7 7 0 0 0 19 15C19 10.5 12 2 12 2Z" fill="#fff" />
      </svg>
    </div>
  </div>
</template>

<script>
const WET_COLOR = "#298abe";

const VARIANT_COLORS = {
  green: "#3d9161",
  orange: "#eb753e",
  gray: "#5c5c5c",
  red: "#b92625",
  // "blue" = pressurizado mas sem o DIR acionando (pivotVariant) — mesmo
  // tom do badge de modo molhado, pra reforçar que há água passando ali.
  blue: WET_COLOR,
};
const DRY_COLOR = "#9e9ea0";
const STOPPED_COLOR = "#1a1a1a";

export default {
  name: "pivot-dial",
  props: {
    angle: { type: Number, default: 0 },
    percent: { type: Number, default: null },
    variant: { type: String, default: "gray" },
    size: { type: Number, default: 72 },
    // Valor cru de "Modo de funcionamento:" do GENERIC (ex: "Molhado"/"Seco").
    workMode: { type: String, default: "" },
    // true quando "Sentido do movimento:" é "Reverso" — o arco do percentímetro
    // varre no sentido anti-horário nesse caso, e horário caso contrário.
    reversed: { type: Boolean, default: false },
    // pirHelpers.pivotPressurized(item) — leitura física do sensor de pressão,
    // válida mesmo parado/Local (out_mask/status não refletem modo Local).
    pressurized: { type: Boolean, default: false },
  },
  computed: {
    color() {
      return VARIANT_COLORS[this.variant] || VARIANT_COLORS.gray;
    },
    // Arco ancorado no ângulo atual do ponteiro (não mais num "topo" fixo), varrendo
    // percent% de um círculo completo na direção do movimento — como não recebemos
    // do firmware um ângulo real de início/parada, isso só ancora visualmente o
    // arco à posição atual em vez de a uma referência arbitrária.
    arcPath() {
      const p = Math.min(100, Math.max(0, this.percent || 0));
      if (p <= 0) return null;
      const cx = 50;
      const cy = 50;
      const r = 42;
      const toRad = (deg) => (deg * Math.PI) / 180;
      const pointAt = (deg) => ({
        x: cx + r * Math.sin(toRad(deg)),
        y: cy - r * Math.cos(toRad(deg)),
      });
      const dir = this.reversed ? 1 : -1;
      const sweepDeg = Math.min(p * 3.6, 359.9);
      const start = pointAt(this.angle);
      const end = pointAt(this.angle + dir * sweepDeg);
      const largeArc = sweepDeg > 180 ? 1 : 0;
      const sweepFlag = dir > 0 ? 1 : 0;
      return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} ${sweepFlag} ${end.x} ${end.y}`;
    },
    normalizedMode() {
      return (this.workMode || "").trim().toLowerCase();
    },
    showModeBadge() {
      return this.normalizedMode === "molhado" || this.normalizedMode === "seco";
    },
    // Pressurizado com o braço parado (variant "blue") é fácil de confundir com
    // "parado de verdade" numa leitura rápida do card — o pulso chama atenção
    // pra área que está sendo irrigada mesmo sem o braço girando.
    pulsing() {
      return this.variant === "blue";
    },
    modeColor() {
      // "gray" é o variant usado (pirHelpers.pivotVariant) quando o PIR está
      // parado (!item.OPEN) — inclui modo Local, onde o painel pode estar
      // bombeando sem o DIR saber. Só força a gota preta se, além de "gray",
      // a leitura física de pressão também não indicar nada pressurizado.
      if (this.variant === "gray" && !this.pressurized) return STOPPED_COLOR;
      return this.normalizedMode === "molhado" ? WET_COLOR : DRY_COLOR;
    },
  },
};
</script>

<style scoped>
.pivot-dial {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}
.pivot-dial-track {
  fill: none;
  stroke: #e2e2e2;
  stroke-width: 6;
}
.pivot-dial-progress {
  fill: none;
  stroke-width: 6;
  stroke-linecap: round;
}
.pivot-dial-needle {
  stroke-width: 3.4;
  stroke-linecap: round;
  transition: transform 0.4s ease;
}
.pivot-dial-hub {
  stroke: #fff;
  stroke-width: 1.5;
}
.pivot-dial-pulse {
  transform-origin: 50px 50px;
  animation: pivot-pulse-ring 1.8s ease-out infinite;
}
@keyframes pivot-pulse-ring {
  0% {
    opacity: 0.45;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(4.5);
  }
}
.pivot-dial-mode-badge {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 36%;
  height: 36%;
  min-width: 14px;
  min-height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pivot-dial-mode-badge--pulse {
  animation: pivot-pulse-badge 1.8s ease-in-out infinite;
}
@keyframes pivot-pulse-badge {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(41, 138, 190, 0.5);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(41, 138, 190, 0);
  }
}
.pivot-dial-mode-icon {
  width: 62%;
  height: 62%;
}
</style>
